import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import { DraggableMessage } from "../components/Draggables";
import InputBoxDec from "../components/InputBoxDec";
import MayaBoxDivision from "../components/MayaBoxDivision.jsx";
import {
  COLOR_DIVISOR,
  COLOR_HIGHLIGHTED,
  FONT,
  MAYA_SYSTEM
} from "../defaults";
import { convertToAlternate, increaseProgress, RenderMayaObjects } from "../helpers";
import {
  buttonCheck,
  level2MayaContinueDistributing,
  level2MayaDropLargerFirst,
  level2MayaExchangeItems,
  level2MayaExchangeTooSmall,
  level2MayaNotEnoughItems,
  level2MayaTooMany,
  level2MayaWrongBox,
  level2MayaWrongDistributed,
  level2MayaWrongNotDistributed,
  levelCorrect,
  levelMayaDivisionWelcome1,
  levelMayaDivisionWelcome2,
  levelMayaDivisionWelcome3,
  levelMayaDivisionWelcome4,
  levelNoInputMulti
} from "../strings";


const LevelNumber = 4;

function calculateDividend(currentProgress) {
  const numbers = [53, 27, 40];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 39) + 20; // Range 20-59. No larger due to spatial constraints.
  }
  return numbers[currentProgress];
}

function calculateDivisor(currentProgress) {
  const numbers = [2, 3, 3];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 1) + 2; // Range 2-3
  }
  return numbers[currentProgress];
}

function Level4Maya({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [dividend, setDividend] = useState(
    calculateDividend(levelProgress[LevelNumber])
  );
  const [divisor, setDivisor] = useState(
    calculateDivisor(levelProgress[LevelNumber])
  );
  const [itemsLeft, setItemsLeft] = useState([]); // How many items to render on the left side.
  const [result, setResult] = useState([]); // How many items should be in each divisor box at the end of the game.
  const [itemsInBoxes, setItemsInBoxes] = useState( // How many items have been distributed so far in each divisor box.
    Array.from({ length: 5 }, () => [0, 0, 0])
  );
  const [exchanged, setExchanged] = useState([false,false,false]); // Whether a certain item type has been exchanged (for another). If this is true, then no items of that type are left over.
  const [distributionState, setDistributionState] = useState(0); // State variable with divisor amount of states (2 or 3). Corresponds to boxIndex. (0 - items equally distributed (default), 1 - first box has just gotten an item. If divisor===3: 2 - second box has just gotten an item (if divisor===2 this is equivalent to state 0, hence not used)) 
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);


  useEffect(() => {
    let divd = calculateDividend(levelProgress[LevelNumber]);
    let divr = calculateDivisor(levelProgress[LevelNumber]);
    if (divd >= 40) { // If two carts are rendered on the left side, then divisor should never be 3, because then way too many baskets will be on screen after an exchange. This should be fine, as it does not lessen the learning effect or enjoyment factor at all.
        divr = 2;
    }
    setDividend(divd);
    setDivisor(divr);
  }, [levelProgress]);

  useEffect(() => { // Initialization, executed each time a new exercise (new dividend & divisor) is loaded.
    let dividendArray = convertToAlternate(MAYA_SYSTEM, dividend);
    let resultArray = convertToAlternate(MAYA_SYSTEM, Math.floor(dividend / divisor));
    setExchanged([false,false,false]);
    setItemsLeft(dividendArray);
    setResult(resultArray); // E.g. if result is 7, this is [2,1,0]
    setItemsInBoxes(Array.from({ length: 5 }, () => [0, 0, 0]));
    setDistributionState(0); // Reset state.
  }, [dividend, divisor]);

  // Hint: Larger index means larger item type. Note that this is inverse to binary and Roman, where smaller index means larger type.
  function handleExchange(index) {
    if (index === 0) { // Case: Exchange unsuccessful, smallest item cannot be exchanged.
      setMessage(level2MayaExchangeTooSmall);
      displayMessage();
      return;
    } 
    if (distributionState > 0) { // Case: Exchange unsuccessful, not all boxes have equal amount of items.
      setMessage(level2MayaContinueDistributing);
      displayMessage();
      return;
    }
    for (let i = 2; index < i; --i) { // Loop to check all indices larger than index. Checks if any larger item types still exist. If they do, exchange is not permitted.
        let someItemsLeft = !exchanged[i] && itemsLeft[i] > 0; // Check if for this larger item type i some items are left. Some items are left iff the items were not exchanged and not all were dropped.
        if (someItemsLeft) { // Case: Exchange unsuccessful, larger items still exist. If for any of the larger item types some items still exist, then user is not allowed to exchange the item type they just tried to exchange.
        setMessage(level2MayaDropLargerFirst);
        displayMessage();
        return; // Exit function
        }
    }
    if (index > 0 && itemsLeft[index] < divisor) { // Case: Exchange successful.
      const newItemsLeft = [...itemsLeft];
      newItemsLeft[index] = 0;
      if (index === 1) {
        newItemsLeft[index - 1] += itemsLeft[index] * 5; // Baskets exchanged for corn plants.
      } else if (index === 2) {
        newItemsLeft[index - 1] += itemsLeft[index] * 4; // Carts exchanged for baskets.
      }
      const newExchanged = [...exchanged];
      newExchanged[index] = true;
      setExchanged(newExchanged);
      setItemsLeft(newItemsLeft);
    } else { // Case: Exchange unsuccessful, enough items to distribute instead.
      setMessage(level2MayaTooMany);
      displayMessage();
    }
  }

  // Hint: Larger index means larger item type. Note that this is inverse to binary and Roman, where smaller index means larger type.
  function handleDropItem(item, boxIndex) { // item[0] is index that corresponds to item type.
    const index = item[0];
    const allBoxesFilled = itemsInBoxes
      .slice(0, divisor)
      .every((box) => box[index] >= result[index]);
    
    for (let i = 2; index < i; --i) { // Loop to check all indices smaller than index. Checks if any larger item types still exist. If they do, drop is not permitted.
      let someItemsLeft = !exchanged[i] && itemsLeft[i] > 0; // Check if for this larger item type i some items are left. Some items are left iff the items were not exchanged and not all were dropped.
      if (someItemsLeft) { // If for any of the larger item types some items still exist, then user is not allowed to drop the item they just tried to drop.
        setMessage(level2MayaDropLargerFirst);
        displayMessage();
        return; // Exit function
      }
    }
    if (allBoxesFilled && index === 0) {
      setMessage(level2MayaNotEnoughItems);
      displayMessage();
    } else if (allBoxesFilled) {
      setMessage(level2MayaExchangeItems);
      displayMessage();
    } else if (itemsInBoxes[boxIndex][index] >= result[index]) {
      setMessage(level2MayaWrongBox);
      displayMessage();
    } else {
      let newItemsLeft = [...itemsLeft];
      newItemsLeft[index] -= 1;
      setItemsLeft(newItemsLeft);
      const newItemsInBoxes = [...itemsInBoxes];
      newItemsInBoxes[boxIndex][index] += 1;
      setItemsInBoxes(newItemsInBoxes);
      setDistributionState(n => ((n+1) % divisor)); // Increment distribution state
    }
  }

  const handleCorrect = () => {
    setCorrect(true);
    setMessage(levelCorrect)
    displayMessage();
  };

  const handleIncorrect = () => {
    let allBoxesFilled = itemsInBoxes
      .slice(0, divisor)
      .every((box) => box[0] >= result[0]);
    allBoxesFilled =
      allBoxesFilled &&
      itemsInBoxes.slice(0, divisor).every((box) => box[1] >= result[1]);
    allBoxesFilled =
      allBoxesFilled &&
      itemsInBoxes.slice(0, divisor).every((box) => box[2] >= result[2]);
    if (allBoxesFilled) {
      setMessage(level2MayaWrongDistributed);
    } else {
      setMessage(level2MayaWrongNotDistributed);
    }
    displayMessage();
  };

  const handleNoInput = () => {
    setMessage(levelNoInputMulti);
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, MAYA_SYSTEM);
    }
    setDisableInput(false);
    setCorrect(false);
  };
 
  const containerArrayWidth = 30; // Used to scale the sizes of containers and items.
 
  const containerArrayStyle = {
    display: (itemsLeft[0] > 0 || itemsLeft[1] > 0 || itemsLeft[2] > 0) ? "flex" : "none", // If no items are rendered, the entire array should disappear completely and make space for the division boxes.
    flexDirection: "row", 
    justifyContent: "center",
    marginRight: "20dvh",
    width: containerArrayWidth+"dvh",
    //backgroundColor: "white"
  };

  const inputBoxStyle = {
    display: "flex", 
    position: "absolute",
    bottom: "3dvh",
    right: (divisor === 3) ? "55%" : "50%", // This way it is nicely centered below the Maya items, regardless of how many boxes (divisor) are displayed.
    justifyContent: "center"
  };
  
  const inputBoxRef = useRef();

  // LLM-generated function.
  const callCheckInput = () => {
    // Call the checkInput function in the child via the ref
    if (inputBoxRef.current) {
      inputBoxRef.current.checkInput();
    }
  };

  return (
    <div>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3dvh" }}
      >
       {levelMayaDivisionWelcome1}<span style={{fontFamily: FONT, color: COLOR_HIGHLIGHTED, fontSize: "4.5dvh"}}>{dividend}</span>{levelMayaDivisionWelcome2}
        <span style={{fontFamily: FONT, color: COLOR_DIVISOR}}>{divisor}</span>
        {levelMayaDivisionWelcome3}<br></br>
        {levelMayaDivisionWelcome4}
      </div>
      
      <DndProvider options={HTML5toTouch}>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div style={{ display: "flex", flexDirection: "column"}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", height: "45dvh"}}>
            <div style={{display: "flex", flexDirection: "column", marginTop: inputBoxStyle.marginTop}}>
              <div style={containerArrayStyle}>
                <RenderMayaObjects droppedItems={itemsLeft} itemSize={4} handleExchange={handleExchange} disableInput={disableInput}/>
              </div>
              <div style={inputBoxStyle}>
                <InputBoxDec
                  handleCorrect={handleCorrect}
                  handleIncorrect={handleIncorrect}
                  handleNoInput = {handleNoInput}
                  dividend={dividend}
                  divisor={divisor}
                  remainder={dividend % divisor}
                  showMessage={showMessage}
                  disableInput={disableInput}
                  ref={inputBoxRef}
                ></InputBoxDec>
              </div>
            </div>
            <div style={{display: "flex", marginTop: containerArrayStyle.marginTop/* Where the boxes are on screen. */}}>
              {Array.from({ length: divisor }).map((_, idx) => (
                <MayaBoxDivision
                  id={idx}
                  droppedItems={itemsInBoxes[idx]}
                  onDropItem={handleDropItem}
                  itemSize={4}
                />
              ))}
            </div>
          </div>
          
          <button
            onClick={callCheckInput}
            className="checkAnswer-button"
            disabled={disableInput}
          >
          {buttonCheck}
          </button>
        </div>
      </DndProvider>
    </div>
  );
}

export default Level4Maya;
